import { apiClient } from '../../../client'

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface OffWeek {
  id: number
  user_id: number
  year_off: number
  week_no: number
  off_day: string
  user_name: string
  user_username: string
  user_email: string
  user_phone: string
  created_by: number
  updated_by: number
  created_at: string
  updated_at: string
}

export interface OffWeekFilters {
  search?: string
  off_day?: string
  year_off?: number
  week_no?: number
}

export interface CreateOffWeekRequest {
  user_id: number
  year_off: number
  week_no: number
  off_day: string
}

export interface UpdateOffWeekRequest extends CreateOffWeekRequest { }

export interface OffWeekExcelScope {
  year_off: number
  week_no: number
}

class OffWeekRepository {
  async findAll(
    page: number = 1,
    limit: number = 10,
    filters?: OffWeekFilters
  ): Promise<PaginatedResponse<OffWeek>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    if (filters?.search) params.append('search', filters.search)
    if (filters?.off_day) params.append('off_day', filters.off_day)
    if (filters?.year_off != null) params.append('year_off', String(filters.year_off))
    if (filters?.week_no != null) params.append('week_no', String(filters.week_no))
    return apiClient.get<PaginatedResponse<OffWeek>>(`/field-force/off-weeks?${params.toString()}`)
  }

  async getById(id: number): Promise<OffWeek | null> {
    return apiClient.get<OffWeek | null>(`/field-force/off-weeks/${id}`)
  }

  async create(data: CreateOffWeekRequest): Promise<OffWeek> {
    return apiClient.post<OffWeek>('/field-force/off-weeks', data)
  }

  async update(id: number, data: UpdateOffWeekRequest): Promise<OffWeek> {
    return apiClient.put<OffWeek>(`/field-force/off-weeks/${id}`, data)
  }

  async delete(id: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/field-force/off-weeks/${id}`)
  }

  async exportToExcel(scope: OffWeekExcelScope): Promise<Blob> {
    const params = new URLSearchParams({
      year_off: String(scope.year_off),
      week_no: String(scope.week_no),
    })
    return apiClient.getBlob(`/field-force/off-weeks/export/excel?${params.toString()}`)
  }

  async exportTemplate(scope: OffWeekExcelScope): Promise<Blob> {
    const params = new URLSearchParams({
      year_off: String(scope.year_off),
      week_no: String(scope.week_no),
    })
    return apiClient.getBlob(`/field-force/off-weeks/export/template?${params.toString()}`)
  }

  async getImportPreview(scope: OffWeekExcelScope): Promise<{ existing_count: number }> {
    const params = new URLSearchParams({
      year_off: String(scope.year_off),
      week_no: String(scope.week_no),
    })
    return apiClient.get<{ existing_count: number }>(`/field-force/off-weeks/import/preview?${params.toString()}`)
  }

  async importFromExcel(
    file: File,
    confirmReplace = false,
  ): Promise<{
    success: boolean
    message: string
    requires_confirmation?: boolean
    existing_count?: number
    data: {
      success: boolean
      total: number
      created: number
      updated: number
      deleted: number
      errors: Array<{ row: number; message: string }>
    }
  }> {
    const params = new URLSearchParams()
    if (confirmReplace) params.append('confirm_replace', '1')
    const formData = new FormData()
    formData.append('file', file)
    const query = params.toString()
    return apiClient.post(
      `/field-force/off-weeks/import/excel${query ? `?${query}` : ''}`,
      formData,
    )
  }
}

export const offWeekRepository = new OffWeekRepository()
